import type { FuseType } from "./types";
import { FUSE_AMP_RANGE } from "./fuse-aic";
import {
  CONTINUOUS_LOAD_HEADROOM,
  FUSE_RATINGS,
} from "./wire-tables";

export const LIFEPO4_FUSE_INSUFFICIENT_MESSAGE =
  "Not enough information to verify a safe fuse.";

export type Lifepo4FuseResultStatus =
  | "inputs-complete"
  | "provisional"
  | "insufficient";

export type Lifepo4FuseCheckStatus = "pass" | "fail" | "missing";

export type Lifepo4FuseCheck = {
  status: Lifepo4FuseCheckStatus;
  message: string;
};

export type Lifepo4FuseMissingField =
  | "maximumContinuousChargeCurrentA"
  | "maximumContinuousDischargeCurrentA"
  | "dischargeSurgeCurrentA"
  | "dischargeSurgeDurationSeconds"
  | "manufacturerFuseGuidance.source"
  | "manufacturerFuseGuidance.minimumFuseForDocumentedSurgeA"
  | "manufacturerFuseGuidance.maximumFuseA"
  | "manufacturerFuseGuidance.recommendedFuseA"
  | "deratedCableAmpacityA"
  | "equipmentMaximumFuseA"
  | "maxDcSystemVoltageV"
  | "exactFuseVoltageRatingV"
  | "exactHolderVoltageRatingV"
  | "exactFusePartNumber"
  | "exactFuseAmpRatingA"
  | "exactHolderPartNumber"
  | "deratedHolderCurrentLimitA"
  | "exactHolderCompatibilitySource"
  | "prospectiveFaultCurrentAtFuseA"
  | "faultCurrentBasis"
  | "exactFuseInterruptRatingA"
  | "candidateFuseFamily"
  | "installationContext.location"
  | "installationContext.standardOrManual"
  | "installationContext.sourceProtectionPlacementVerified"
  | "bankConfiguration"
  | "wholeBankProspectiveFaultCurrentAtFuseA";

/**
 * Evidence taken from the protected equipment's manual or fuse schedule.
 *
 * `minimumFuseForDocumentedSurgeA` is the smallest fuse rating that the
 * manufacturer's guidance or the exact fuse's time-current curve says will
 * carry the supplied surge current for the supplied duration. The calculator
 * deliberately does not invent a generic time-current curve from peak amps.
 */
export type Lifepo4ManufacturerFuseGuidance = {
  source?: string;
  minimumFuseForDocumentedSurgeA?: number;
  maximumFuseA?: number;
  /** An exact recommendation takes precedence over rounding to the generic
   * standard-rating ladder, but never over the manufacturer's maximum. */
  recommendedFuseA?: number;
};

export type Lifepo4FuseInstallationContext = {
  location?: string;
  standardOrManual?: string;
  /** The caller has checked source proximity and any permitted exception
   * against the named standard/manual. This module cannot infer placement. */
  sourceProtectionPlacementVerified?: boolean;
};

export type Lifepo4BankConfiguration = {
  batteryCount: number;
  parallelStrings: number;
  /** A multi-battery source must use the prospective current for the complete
   * installed configuration. A per-battery value cannot pass the check. */
  prospectiveFaultCurrentScope: "single-battery" | "whole-bank";
};

export type Lifepo4FuseCalculatorInput = {
  /** Display-only context. Amp-hours never size a fuse or prove AIC. */
  batteryCapacityAh?: number;
  bank?: Lifepo4BankConfiguration;

  /** Worst credible simultaneous current through this fuse in each direction.
   * Both directions are explicit so a lightly loaded bank with large chargers
   * cannot be under-sized from load current alone. */
  maximumContinuousChargeCurrentA?: number;
  maximumContinuousDischargeCurrentA?: number;
  dischargeSurgeCurrentA?: number;
  dischargeSurgeDurationSeconds?: number;
  manufacturerFuseGuidance?: Lifepo4ManufacturerFuseGuidance;

  /** Ampacity after all installation-specific temperature/bundling derating. */
  deratedCableAmpacityA?: number;
  /** Optional additional ceiling for another protected device in the run. */
  equipmentMaximumFuseA?: number;

  /** Maximum charging/equalisation/transient DC voltage, not nominal voltage. */
  maxDcSystemVoltageV?: number;
  exactFusePartNumber?: string;
  exactFuseAmpRatingA?: number;
  exactFuseVoltageRatingV?: number;
  exactHolderPartNumber?: string;
  /** Holder current limit after its maker's ambient, enclosure, mounting and
   * terminal-condition derating for the proposed installation. */
  deratedHolderCurrentLimitA?: number;
  exactHolderVoltageRatingV?: number;
  /** Datasheet or maker table confirming that this holder accepts the exact
   * fuse family and rating being evaluated. */
  exactHolderCompatibilitySource?: string;

  /** Documented or calculated prospective current at the proposed fuse
   * location, based on manufacturer source data for the complete bank and the
   * installed path—not a value inferred from Ah or BMS cutoff current. */
  prospectiveFaultCurrentAtFuseA?: number;
  faultCurrentBasis?: string;
  /** Interrupt rating of the exact fuse at the relevant DC voltage. */
  exactFuseInterruptRatingA?: number;

  /** A family being evaluated, never an automatically prescribed product. */
  candidateFuseFamily?: FuseType;
  installationContext?: Lifepo4FuseInstallationContext;
};

export type Lifepo4FuseCalculatorResult = {
  status: Lifepo4FuseResultStatus;
  summary: string;
  candidateAmpRatingA?: number;
  /** Emitted only after every check passes. It remains a family to verify, not
   * a product recommendation. */
  candidateFamilyToVerify?: FuseType;
  sizing: {
    continuousBaselineA?: number;
    maximumContinuousCurrentA?: number;
    governingContinuousDirection?: "charge" | "discharge" | "equal";
    surgeBaselineA?: number;
    baselineA?: number;
    governingConstraint?: "continuous" | "documented-surge";
    effectiveCeilingA?: number;
    usedManufacturerRecommendation: boolean;
    usedStandardRatingLadder: boolean;
  };
  checks: {
    ampRating: Lifepo4FuseCheck;
    voltageRating: Lifepo4FuseCheck;
    interruptRating: Lifepo4FuseCheck;
    installationContext: Lifepo4FuseCheck;
  };
  missingFields: Lifepo4FuseMissingField[];
  bank: {
    batteryCapacityAh?: number;
    batteryCount: number;
    parallelStrings: number;
    prospectiveFaultCurrentScope: "single-battery" | "whole-bank";
    capacityUsedForSizing: false;
  };
  notes: string[];
};

const missingCheck = (message: string): Lifepo4FuseCheck => ({
  status: "missing",
  message,
});

const isPositive = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const isNonNegative = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isContinuousCurrent = (value: unknown): value is number =>
  isNonNegative(value) && Number.isFinite(value * CONTINUOUS_LOAD_HEADROOM);

const isNonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

function nextStandardRating(baselineA: number, ceilingA: number): number | undefined {
  return FUSE_RATINGS.find(
    (ratingA) => ratingA >= baselineA && ratingA <= ceilingA,
  );
}

function baseResult(input: Lifepo4FuseCalculatorInput): Lifepo4FuseCalculatorResult {
  const bank = input.bank;
  return {
    status: "insufficient",
    summary: LIFEPO4_FUSE_INSUFFICIENT_MESSAGE,
    sizing: {
      usedManufacturerRecommendation: false,
      usedStandardRatingLadder: false,
    },
    checks: {
      ampRating: missingCheck("Current, surge, manufacturer, and cable limits are required."),
      voltageRating: missingCheck("The maximum system, exact fuse, and exact holder DC ratings are required."),
      interruptRating: missingCheck("Prospective fault current at the fuse location and the exact fuse interrupt rating are required."),
      installationContext: missingCheck("Installation location and its governing standard or manual are required."),
    },
    missingFields: [],
    bank: {
      ...(isPositive(input.batteryCapacityAh)
        ? { batteryCapacityAh: input.batteryCapacityAh }
        : {}),
      batteryCount: bank?.batteryCount ?? 1,
      parallelStrings: bank?.parallelStrings ?? 1,
      prospectiveFaultCurrentScope:
        bank?.prospectiveFaultCurrentScope ?? "whole-bank",
      capacityUsedForSizing: false,
    },
    notes: [
      "Battery capacity (Ah) is not load current and is never used to choose the fuse amp rating.",
      "Amp rating and DC interrupt rating are independent checks.",
    ],
  };
}

/**
 * Produces an evidence-bounded LiFePO4 fuse candidate. It fails closed: no
 * amp-hour short-circuit fallback, no inferred fuse time-current curve, and no
 * family recommendation from family-wide nominal data.
 */
export function calculateLifepo4Fuse(
  input: Lifepo4FuseCalculatorInput,
): Lifepo4FuseCalculatorResult {
  const result = baseResult(input);
  const missing = result.missingFields;
  const guidance = input.manufacturerFuseGuidance;

  if (!isContinuousCurrent(input.maximumContinuousChargeCurrentA)) {
    missing.push("maximumContinuousChargeCurrentA");
  }
  if (!isContinuousCurrent(input.maximumContinuousDischargeCurrentA)) {
    missing.push("maximumContinuousDischargeCurrentA");
  }
  if (!isPositive(input.dischargeSurgeCurrentA)) {
    missing.push("dischargeSurgeCurrentA");
  }
  if (!isPositive(input.dischargeSurgeDurationSeconds)) {
    missing.push("dischargeSurgeDurationSeconds");
  }
  if (!isNonEmpty(guidance?.source)) {
    missing.push("manufacturerFuseGuidance.source");
  }
  if (!isPositive(guidance?.minimumFuseForDocumentedSurgeA)) {
    missing.push("manufacturerFuseGuidance.minimumFuseForDocumentedSurgeA");
  }
  if (!isPositive(guidance?.maximumFuseA)) {
    missing.push("manufacturerFuseGuidance.maximumFuseA");
  }
  if (!isPositive(input.deratedCableAmpacityA)) {
    missing.push("deratedCableAmpacityA");
  }
  // Optional means absent, not invalid. Discarding a supplied bad limit would
  // turn a conflicting or unfinished form into an apparently verified fuse.
  const invalidEquipmentCeiling =
    input.equipmentMaximumFuseA != null && !isPositive(input.equipmentMaximumFuseA);
  const invalidRecommendation =
    guidance?.recommendedFuseA != null && !isPositive(guidance.recommendedFuseA);
  if (invalidEquipmentCeiling) missing.push("equipmentMaximumFuseA");
  if (invalidRecommendation) missing.push("manufacturerFuseGuidance.recommendedFuseA");

  const hasBothContinuousDirections =
    isContinuousCurrent(input.maximumContinuousChargeCurrentA) &&
    isContinuousCurrent(input.maximumContinuousDischargeCurrentA);
  const maximumContinuousCurrentA = hasBothContinuousDirections
    ? Math.max(
        input.maximumContinuousChargeCurrentA!,
        input.maximumContinuousDischargeCurrentA!,
      )
    : undefined;
  const continuousBaselineA =
    maximumContinuousCurrentA != null && maximumContinuousCurrentA > 0
      ? maximumContinuousCurrentA * CONTINUOUS_LOAD_HEADROOM
      : undefined;
  const surgeBaselineA = isPositive(guidance?.minimumFuseForDocumentedSurgeA)
    ? guidance.minimumFuseForDocumentedSurgeA
    : undefined;
  const baselineA =
    continuousBaselineA != null && surgeBaselineA != null
      ? Math.max(continuousBaselineA, surgeBaselineA)
      : undefined;
  const ceilingCandidates = [
    guidance?.maximumFuseA,
    input.deratedCableAmpacityA,
    input.equipmentMaximumFuseA,
  ].filter(isPositive);
  const effectiveCeilingA =
    ceilingCandidates.length > 0 ? Math.min(...ceilingCandidates) : undefined;

  result.sizing.continuousBaselineA = continuousBaselineA;
  result.sizing.maximumContinuousCurrentA = maximumContinuousCurrentA;
  if (hasBothContinuousDirections) {
    result.sizing.governingContinuousDirection =
      input.maximumContinuousChargeCurrentA! >
      input.maximumContinuousDischargeCurrentA!
        ? "charge"
        : input.maximumContinuousDischargeCurrentA! >
            input.maximumContinuousChargeCurrentA!
          ? "discharge"
          : "equal";
  }
  result.sizing.surgeBaselineA = surgeBaselineA;
  result.sizing.baselineA = baselineA;
  result.sizing.effectiveCeilingA = effectiveCeilingA;
  if (baselineA != null && continuousBaselineA != null && surgeBaselineA != null) {
    result.sizing.governingConstraint =
      surgeBaselineA > continuousBaselineA ? "documented-surge" : "continuous";
  }

  let ampFailure =
    invalidEquipmentCeiling || invalidRecommendation
      ? "An entered equipment ceiling or manufacturer recommendation is invalid. Enter a positive, finite amp rating or leave the optional field blank."
      : undefined;
  let candidateAmpRatingA: number | undefined;
  if (baselineA != null) {
    if (effectiveCeilingA != null && baselineA > effectiveCeilingA) {
      ampFailure = `The ${baselineA} A baseline exceeds the ${effectiveCeilingA} A conductor/equipment ceiling.`;
    } else if (isPositive(guidance?.recommendedFuseA)) {
      candidateAmpRatingA = guidance.recommendedFuseA;
      result.sizing.usedManufacturerRecommendation = true;
      if (candidateAmpRatingA < baselineA) {
        ampFailure = `The ${candidateAmpRatingA} A manufacturer recommendation is below the ${baselineA} A load/surge baseline.`;
      } else if (
        effectiveCeilingA != null &&
        candidateAmpRatingA > effectiveCeilingA
      ) {
        ampFailure = `The ${candidateAmpRatingA} A manufacturer recommendation exceeds the ${effectiveCeilingA} A conductor/equipment ceiling.`;
      }
    } else {
      candidateAmpRatingA = nextStandardRating(
        baselineA,
        effectiveCeilingA ?? Number.POSITIVE_INFINITY,
      );
      result.sizing.usedStandardRatingLadder = true;
      if (candidateAmpRatingA == null) {
        ampFailure = "No rating on the supported standard fuse ladder satisfies the load/surge baseline and known ceilings.";
      }
    }
  }

  const family =
    typeof input.candidateFuseFamily === "string" &&
    Object.hasOwn(FUSE_AMP_RANGE, input.candidateFuseFamily)
      ? input.candidateFuseFamily
      : undefined;
  if (!family) missing.push("candidateFuseFamily");
  if (!isNonEmpty(input.exactFusePartNumber)) missing.push("exactFusePartNumber");
  if (!isPositive(input.exactFuseAmpRatingA)) missing.push("exactFuseAmpRatingA");
  if (!isNonEmpty(input.exactHolderPartNumber)) {
    missing.push("exactHolderPartNumber");
  }
  if (!isPositive(input.deratedHolderCurrentLimitA)) {
    missing.push("deratedHolderCurrentLimitA");
  }
  if (!isNonEmpty(input.exactHolderCompatibilitySource)) {
    missing.push("exactHolderCompatibilitySource");
  }

  if (candidateAmpRatingA != null && family) {
    const familyRange = FUSE_AMP_RANGE[family];
    if (
      candidateAmpRatingA < familyRange.min ||
      candidateAmpRatingA > familyRange.max
    ) {
      ampFailure = `${candidateAmpRatingA} A is outside the modeled ${input.candidateFuseFamily} family range of ${familyRange.min}–${familyRange.max} A.`;
    }
  }
  if (
    candidateAmpRatingA != null &&
    isPositive(input.exactFuseAmpRatingA) &&
    input.exactFuseAmpRatingA !== candidateAmpRatingA
  ) {
    ampFailure = `The exact fuse is rated ${input.exactFuseAmpRatingA} A, not the calculated ${candidateAmpRatingA} A candidate.`;
  }
  if (
    candidateAmpRatingA != null &&
    isPositive(input.deratedHolderCurrentLimitA) &&
    input.deratedHolderCurrentLimitA < candidateAmpRatingA
  ) {
    ampFailure = `The installed holder's derated ${input.deratedHolderCurrentLimitA} A limit is below the ${candidateAmpRatingA} A candidate.`;
  }

  const exactAmpDeviceEvidenceComplete =
    family != null &&
    isNonEmpty(input.exactFusePartNumber) &&
    isPositive(input.exactFuseAmpRatingA) &&
    isNonEmpty(input.exactHolderPartNumber) &&
    isPositive(input.deratedHolderCurrentLimitA) &&
    isNonEmpty(input.exactHolderCompatibilitySource);

  if (ampFailure) {
    candidateAmpRatingA = undefined;
    result.checks.ampRating = { status: "fail", message: ampFailure };
  } else if (candidateAmpRatingA == null) {
    result.checks.ampRating = missingCheck(
      "Continuous current and manufacturer-backed surge guidance are required before an amp rating can be calculated.",
    );
  } else if (
    !isPositive(guidance?.maximumFuseA) ||
    !isPositive(input.deratedCableAmpacityA) ||
    !isNonEmpty(guidance?.source) ||
    !exactAmpDeviceEvidenceComplete
  ) {
    result.checks.ampRating = missingCheck(
      `${candidateAmpRatingA} A is only provisional until the manufacturer limits, derated conductor ceiling, exact fuse, and compatible exact holder are supplied.`,
    );
  } else {
    result.checks.ampRating = {
      status: "pass",
      message: `The entered ${candidateAmpRatingA} A exact fuse matches the calculated candidate and stays within the entered conductor, equipment, family and holder limits.`,
    };
  }
  result.candidateAmpRatingA = candidateAmpRatingA;

  if (!isPositive(input.maxDcSystemVoltageV)) missing.push("maxDcSystemVoltageV");
  if (!isPositive(input.exactFuseVoltageRatingV)) {
    missing.push("exactFuseVoltageRatingV");
  }
  if (!isPositive(input.exactHolderVoltageRatingV)) {
    missing.push("exactHolderVoltageRatingV");
  }
  if (
    isPositive(input.maxDcSystemVoltageV) &&
    isPositive(input.exactFuseVoltageRatingV) &&
    isPositive(input.exactHolderVoltageRatingV)
  ) {
    const weakestVoltageRatingV = Math.min(
      input.exactFuseVoltageRatingV,
      input.exactHolderVoltageRatingV,
    );
    result.checks.voltageRating =
      weakestVoltageRatingV >= input.maxDcSystemVoltageV
        ? {
            status: "pass",
            message: `Both exact-device ratings cover the ${input.maxDcSystemVoltageV} V maximum DC system voltage.`,
          }
        : {
            status: "fail",
            message: `The ${weakestVoltageRatingV} V weakest exact-device rating is below the ${input.maxDcSystemVoltageV} V maximum DC system voltage.`,
          };
  }

  if (!isPositive(input.prospectiveFaultCurrentAtFuseA)) {
    missing.push("prospectiveFaultCurrentAtFuseA");
  }
  if (!isNonEmpty(input.faultCurrentBasis)) {
    missing.push("faultCurrentBasis");
  }
  if (!isPositive(input.exactFuseInterruptRatingA)) {
    missing.push("exactFuseInterruptRatingA");
  }
  const bank = input.bank;
  let bankConfigurationValid = true;
  if (bank) {
    bankConfigurationValid =
      Number.isSafeInteger(bank.batteryCount) &&
      bank.batteryCount > 0 &&
      Number.isSafeInteger(bank.parallelStrings) &&
      bank.parallelStrings > 0 &&
      bank.parallelStrings <= bank.batteryCount &&
      bank.batteryCount % bank.parallelStrings === 0 &&
      (bank.prospectiveFaultCurrentScope === "whole-bank" ||
        bank.prospectiveFaultCurrentScope === "single-battery");
    if (!bankConfigurationValid) missing.push("bankConfiguration");
    if (
      bankConfigurationValid &&
      bank.batteryCount > 1 &&
      bank.prospectiveFaultCurrentScope !== "whole-bank"
    ) {
      missing.push("wholeBankProspectiveFaultCurrentAtFuseA");
    }
  }

  const faultCurrentCoversBank =
    bankConfigurationValid &&
    (!bank ||
      bank.batteryCount <= 1 ||
      bank.prospectiveFaultCurrentScope === "whole-bank");
  if (
    isPositive(input.prospectiveFaultCurrentAtFuseA) &&
    isPositive(input.exactFuseInterruptRatingA) &&
    isNonEmpty(input.faultCurrentBasis) &&
    faultCurrentCoversBank
  ) {
    result.checks.interruptRating =
      input.exactFuseInterruptRatingA >=
      input.prospectiveFaultCurrentAtFuseA
        ? {
            status: "pass",
            message: `The exact fuse's ${input.exactFuseInterruptRatingA} A interrupt rating covers the entered ${input.prospectiveFaultCurrentAtFuseA} A prospective current at this fuse location.`,
          }
        : {
            status: "fail",
            message: `The exact fuse's ${input.exactFuseInterruptRatingA} A interrupt rating is below the entered ${input.prospectiveFaultCurrentAtFuseA} A prospective current at this fuse location.`,
          };
  } else if (!faultCurrentCoversBank) {
    result.checks.interruptRating = missingCheck(
      "A documented or calculated prospective fault current at this location for the complete installed bank/source is required; per-battery current cannot prove AIC.",
    );
  }

  const context = input.installationContext;
  if (!isNonEmpty(context?.location)) {
    missing.push("installationContext.location");
  }
  if (!isNonEmpty(context?.standardOrManual)) {
    missing.push("installationContext.standardOrManual");
  }
  if (context?.sourceProtectionPlacementVerified !== true) {
    missing.push("installationContext.sourceProtectionPlacementVerified");
  }
  if (
    isNonEmpty(context?.location) &&
    isNonEmpty(context.standardOrManual) &&
    context.sourceProtectionPlacementVerified === true
  ) {
    result.checks.installationContext = {
      status: "pass",
      message: `Source-side placement was checked for ${context.location} against ${context.standardOrManual}.`,
    };
  }

  const anyFailed = Object.values(result.checks).some(
    (check) => check.status === "fail",
  );
  if (anyFailed) {
    result.status = "insufficient";
    result.summary =
      missing.length > 0
        ? LIFEPO4_FUSE_INSUFFICIENT_MESSAGE
        : "The supplied limits conflict, so this calculator cannot verify a fuse candidate.";
    result.candidateAmpRatingA = undefined;
    return result;
  }

  if (missing.length > 0 || candidateAmpRatingA == null) {
    result.status = candidateAmpRatingA == null ? "insufficient" : "provisional";
    result.summary = LIFEPO4_FUSE_INSUFFICIENT_MESSAGE;
    return result;
  }

  result.status = "inputs-complete";
  result.summary = `The entered limits are internally consistent with a ${candidateAmpRatingA} A candidate. This does not verify the installation.`;
  result.candidateFamilyToVerify = family;
  result.notes.push(
    `${input.candidateFuseFamily} is a candidate family to verify, not a prescriptive product recommendation.`,
  );
  return result;
}
